import * as React from 'react';
import style from './main.less';
import './burgerMenu.css';
import { AdminMainContent } from './AdminMainContent';
import { ApiService } from '../services/api_service';
import { ContextService } from '../services/context_service';
import { DataService } from '../services/data_service';
import { getRoutingFromLocationHash, setRoutingToLocationHash } from '../main/functions/locationHash';
import { IDs } from '../helpers/IDs';
import { lazyInject } from '../services/inversify';
import { LoginForm } from '../login/loginForm';
import { slide as Menu } from 'react-burger-menu';
import { RechteService } from '../services/rechte_service';
import { RoutingIDs } from '../helpers/RoutingAdminIDs';
import { SectionStyle } from '../helpers/SectionStyle';
import { StructureService } from '../services/structure_service';
import * as config from '../wips.config.js';
import { WActionButton } from '../core/elements/WActionButton';
import { getLanguageSelect } from '../i18n/getLanguageSelect';
import { i18nLang } from '../i18n/i18nLang';
import { i18n } from '../i18n/i18n';

interface AdminMainState extends React.ClassAttributes<any> {
    hiddenMenu: boolean;
    structureDone: boolean;
    userName: string;
    hasToken: boolean;
    rechteDone: boolean;
    routing: any;
    locationHash?: string;
    lang: string;
    showLanguages: boolean;
    loadMehrsprachigkeitDone: boolean;
}

export class AdminMain extends React.Component<{}, AdminMainState> {

    constructor(props: any) {
        super(props);

        ApiService.module = IDs.MOD_ETOOL_ADMIN;

        this.state = {
            hiddenMenu: false,
            structureDone: false,
            userName: '',
            hasToken: false,
            rechteDone: false,
            routing: {},
            lang: i18nLang.getUILang(),
            showLanguages: false,
            loadMehrsprachigkeitDone: false
        };

        this.handleStateChange = this.handleStateChange.bind(this);
        this.initUser = this.initUser.bind(this);
        this.onStructureReceive = this.onStructureReceive.bind(this);
        this.prepareSystem = this.prepareSystem.bind(this);
        this.setRouting = this.setRouting.bind(this);
    }

    private structCount = 0;
    private structCountExpected = 4;

    @lazyInject(ApiService) private apiService: ApiService;
    @lazyInject(DataService) private dataService: DataService;
    @lazyInject(ContextService) private contextService: ContextService;
    @lazyInject(RechteService) private rechteService: RechteService;
    @lazyInject(StructureService) private structureService: StructureService;

    componentDidMount(): void {
        const cookie = document.cookie;
        const parts = cookie.split(';');
        let token;

        parts.forEach((p: string) => {
            const arr = p.split('=');
            const name = arr[0].trim();
            const value = arr[1];
            if (name === 'admin_access_token') {
                token = value;
            }
        });
        if (token && token !== '') {
            this.setState({ hasToken: true });
            this.checkAccessToken(token);
        }
        // WKETOOL-113: 182 Browser-Navigation
        this.initLocationHash();
    }

    componentDidUpdate(prevProps: Readonly<{}>, prevState: Readonly<AdminMainState>, snapshot?: any): void {
        if (!this.state.hasToken) {
            SectionStyle.curSectionId = 1;
        }
        // WKETOOL-206: Mehrsprachigkeit
        if (config.devFeaturesMap.N207_MEHRSPRACHIGKEIT && this.state.userName && !this.state.loadMehrsprachigkeitDone) {
            this.setState({ loadMehrsprachigkeitDone: true });
            this.loadMehrsprachigkeit();
        }
    }

    loadMehrsprachigkeit() {
        this.dataService.loadData(IDs.ENT_MEHRSPRACHIGKEIT, {}, {}, (res: any) => {
            i18nLang.rows = res[IDs.ENT_MEHRSPRACHIGKEIT];
            i18nLang.init();
            i18nLang.cbUpdateUI = () => {
                const lang = this.state.lang;
                this.setState({ lang: '' }, () => this.setState({ lang }));
            };
        }, IDs.MOD_ETOOL_ADMIN);
    }

    addEventListenerOnHashchange() {
        window.addEventListener('hashchange', () => {
            const routing = getRoutingFromLocationHash(window.location.hash, this.contextService);
            if (routing) {
                this.setRouting(routing, true);
            }
        });
    }

    checkAccessToken(token: string): void {
        this.apiService.call(
            'checkAccessToken', { check_token: token, module: IDs.MOD_ETOOL_ADMIN, adminOnly: true },
            (json: string, suc: boolean) => {
                // console.log(json, suc);
                if (suc && json) {
                    let obj;
                    try {
                        obj = JSON.parse(json);
                    } catch (e) {
                        console.error(e, json);
                    }
                    if (obj) {
                        const userName = obj.login || obj.user;
                        this.initUser(userName, token, obj.bereich_key, obj.all_bereich_keys);
                    } else {
                        this.setState({ hasToken: false });
                        document.cookie = 'admin_access_token= ; path=/';
                    }
                } else {
                    this.setState({ hasToken: false });
                    document.cookie = 'admin_access_token= ; path=/';
                }
            }
        );
    }

    handleStateChange(state: any): void {
        this.setState({ hiddenMenu: state.isOpen });
    }

    initLocationHash(): void {
        this.addEventListenerOnHashchange();
        setTimeout(() => this.setRouting({ path: RoutingIDs.ROUTE_DATENVERWALTUNG }));
    }

    initUser(userName: string, token?: string, bereich_key?: string, all_bereich_keys?: Array<string>): void {
        const date = new Date();
        date.setTime(date.getTime() + (1 * 24 * 3600 * 1000));

        this.apiService.setUsername(userName);
        this.apiService.setBereichKey(bereich_key);
        this.apiService.setMultiBereichKeys(all_bereich_keys);

        if (token && token !== '') {
            document.cookie = 'admin_access_token=' + token + '; expires=' + date.toUTCString() + '; path=/';
            this.apiService.setToken(token);
            this.apiService.setAdminFlag(true);
            this.setState({ hasToken: true });
        }
        this.setState({ userName });
        SectionStyle.curSectionId = 0;
        this.prepareSystem();
    }

    onStructureReceive(data: ReadonlyArray<any>): void {
        // console.log(data);
        // Wir machen nichts, wir wissen nun nur, dass die Daten im Service vorliegen
        // Falls der Call nicht klappt, müssen wir noch was zum Retry bauen.
        this.structCount++;

        if (this.structCount === this.structCountExpected) {
            const structureDone = true;
            this.setState({ structureDone });
        }
    }

    prepareSystem(): void {
        this.structureService.getStructure('core', this.onStructureReceive, false);
        this.structureService.getStructure('legacy', this.onStructureReceive, false);
        this.structureService.getStructure(IDs.MOD_ETOOL_ADMIN, this.onStructureReceive, false);
        this.structureService.getStructure(IDs.MOD_ETOOL, this.onStructureReceive, false);
        this.rechteService.loadRechte(() => this.setState({ rechteDone: true }));
    }

    render(): JSX.Element {
        const { hasToken, rechteDone, userName, structureDone } = this.state;

        const showTabsAll = this.rechteService.hasRecht('admin.tab.all');
        const showTabStart = showTabsAll || this.rechteService.hasRecht('admin.tab.startseite');
        const showTabDaten = showTabsAll || this.rechteService.hasRecht('admin.tab.datenverwaltung');
        const showTabKennzahlen = showTabsAll || this.rechteService.hasRecht('admin.tab.kennzahlen_datenbank');
        const showTabUnternehmen = showTabsAll || this.rechteService.hasRecht('admin.tab.unternehmensdatenbank');
        const showTabRechte = showTabsAll || this.rechteService.hasRecht('admin.tab.rechteverwaltung');
        const showTabPV = showTabsAll || this.rechteService.hasRecht('admin.tab.pv_tool');
        const showTabInfo = showTabsAll || this.rechteService.hasRecht('admin.tab.info_mail');
        const showTabHilfe = showTabsAll || this.rechteService.hasRecht('admin.tab.online_hilfe');

        const img = require('../img/LogoKlein.png');
        const img2 = require('../img/LogoAdminFinal.png');
        const img3 = require('../img/LogoMenue.png');

        return (
            <div>
                {!hasToken &&
                    <div className={style.loginWrapper}>
                        <LoginForm
                            adminMode={true}
                            img={img}
                            img2={img2}
                            setUserToken={(userName: string, auth: any) =>
                                this.initUser(
                                    userName,
                                    auth ? auth.access_token : null,
                                    auth ? auth.bereich_key : null,
                                    auth ? auth.all_bereich_keys : null
                                )
                            }
                        />
                    </div>
                }
                {userName && structureDone && rechteDone &&
                    <div className='cont-administration'>
                        <div className={style.topWrapper}>
                            <div className={style.logoWrapper}>
                                <img src={img} alt='Logo' height={40} />
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 'bold', padding: 10 }}>
                                E-Tool Administration und Datenverwaltung
                            </div>
                            <div style={{ width: 100 }}>
                                <div
                                    style={{
                                        display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', position: 'absolute',
                                        right: 50, marginTop: 5
                                    }}
                                >
                                    {config.devFeaturesMap.N207_MEHRSPRACHIGKEIT && this.state.loadMehrsprachigkeitDone &&
                                        <div style={{ textAlign: 'center', zIndex: 100 }}>
                                            {!this.state.showLanguages &&
                                                <WActionButton
                                                    label={i18n('Sprache', 'sys')}
                                                    tooltip={i18n('Sprache', 'sys')}
                                                    key={`lang_${this.state.lang}`}
                                                    action={`lang_${this.state.lang}`}
                                                    style='big'
                                                    styleProps={{ backgroundSize: '35px 35px' }}
                                                    onAction={() => this.setState({ showLanguages: true })}
                                                />
                                            }
                                            {this.state.showLanguages &&
                                                getLanguageSelect(this.state.lang, (lang: string) => {
                                                    // window.location.reload();
                                                    this.setState({ showLanguages: false });
                                                    if (this.state.lang !== lang) {
                                                        this.setState({ lang });
                                                        i18nLang.setLocales();
                                                    }
                                                })
                                            }
                                        </div>
                                    }
                                </div>
                                <Menu
                                    right
                                    isOpen={this.state.hiddenMenu}
                                    onStateChange={(state: any) => this.handleStateChange(state)}
                                >
                                    <span style={{ width: 0 }} />
                                    <img src={img3} alt='Logo' width={240} style={{ marginBottom: 20 }} />
                                    <span className='userName' >{`${i18n('Benutzer', 'sys')}: ${this.state.userName}`}</span>
                                    <hr style={{ color: '#666' }} />
                                    {showTabStart && <div
                                        className='menu-item'
                                        onClick={() => this.setRouting({ path: RoutingIDs.ROUTE_START })}
                                    >
                                        <p>{i18n('START', 'tab')}</p>
                                    </div>}
                                    {showTabDaten && <div
                                        className='menu-item'
                                        onClick={() => this.setRouting({ path: RoutingIDs.ROUTE_DATENVERWALTUNG })}
                                    >
                                        <p>{i18n('Datenverwaltung', 'tab')}</p>
                                    </div>}
                                    {showTabKennzahlen && <div
                                        className='menu-item'
                                        onClick={() => this.setRouting({ path: RoutingIDs.ROUTE_KENNZAHLEN_DATENBANK })}
                                    >
                                        <p>{i18n('Kennzahlen-Datenbank', 'tab')}</p>
                                    </div>}
                                    {showTabUnternehmen && <div
                                        className='menu-item'
                                        onClick={() => this.setRouting({ path: RoutingIDs.ROUTE_UNTERNEHMENSDATENBANK })}
                                    >
                                        <p>{i18n('Unternehmensdatenbank', 'tab')}</p>
                                    </div>}
                                    {showTabRechte && <div
                                        className='menu-item'
                                        onClick={() => this.setRouting({ path: RoutingIDs.ROUTE_RECHTEVERWALTUNG })}
                                    >
                                        <p>{i18n('Rechteverwaltung', 'tab')}</p>
                                    </div>}
                                    {showTabPV && <div
                                        className='menu-item'
                                        onClick={() => this.setRouting({ path: RoutingIDs.ROUTE_PV_TOOL })}
                                    >
                                        <p>{i18n('PV-Tool', 'tab')}</p>
                                    </div>}
                                    <div
                                        className='menu-item'
                                        onClick={() => {
                                            if (window.confirm(i18n('Wirklich ausloggen?', 'sys'))) {
                                                document.cookie = 'admin_access_token= ; path=/';
                                                window.location.reload();
                                            }
                                        }}
                                    >
                                        <p>{i18n('Ausloggen', 'sys')}</p>
                                    </div>
                                </Menu>
                            </div>
                        </div>
                        <div className={style.contentWrapper}>
                            <AdminMainContent
                                onRoutingChange={this.setRouting}
                                routing={this.state.routing}
                                lang={this.state.lang}
                            />
                        </div>
                    </div>
                }
            </div>
        );
    }

    setRouting(routing: any, fromLocationHash: boolean = false): void {
        if (!!Object.keys(routing || {}).length && JSON.stringify(routing) !== JSON.stringify(this.state.routing)) {
            // console.info('setRouting', routing);
            this.setState({ routing, ...(this.state.hiddenMenu && { hiddenMenu: false }) });

            if (routing.path && !routing.path.hasSubs && !fromLocationHash) {
                setRoutingToLocationHash(routing, this.contextService);
            }
        }
    }
}
